import {
  Autocomplete,
  FormControl,
  FormHelperText,
  TextField,
} from "@mui/material";
import _ from "lodash";
import { useCallback, useMemo, type FC } from "react";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useDynamicFormsTranslate, useSelectItemsTranslate } from "@ryde/redux";
import {
  checkCleanupNestedValidation,
  excludeNotArrayValue,
  Log,
  ValidationHelpers,
  type ICustomField,
} from "@ryde/shared";

import { FullWidthTextFieldWrapper } from "../parts-styled";
import { FieldExplanationIcon } from "./_parts";
import {
  DropdownOptionRenderer,
  getDropdownValuesLabels,
  ListBox,
  TechnicalNameChip,
  useTechnicalNameOptions,
} from "./dropdowns";
import { SelectAllPaperWrapper } from "./selectall-paper-wrapper.template";

export const MultiselectTemplate: FC<ICustomField> = ({
  id,
  name,
  formAttributes,
  formLabel,
  formValidation,
  labelTranslated,
  e2eData,
}) => {
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
    translatedText: labelTranslated,
  });
  const {
    selectItems,
    disabled,
    skipDropdownItemsTranslation,
    translationPath,
    disableSorting,
  } = formAttributes || {};
  const initial: number[] = getValues(name as string);

  if (initial && !_.isArray(initial)) {
    Log.error(t("errors.multiSelectBoundTemplateInitialValueNotArray"));
  }

  const { translateLabel } = useSelectItemsTranslate();
  const [options, mapOptionLabel] = useMemo(() => {
    return getDropdownValuesLabels({
      dropDownItems: selectItems,
      transformLabelFn: skipDropdownItemsTranslation
        ? undefined
        : ({ label }) => translateLabel(label, translationPath),
    });
  }, [
    selectItems,
    skipDropdownItemsTranslation,
    translateLabel,
    translationPath,
  ]);

  const getOptionLabel = useCallback(
    (value: unknown) => String(mapOptionLabel.get(value as string | number)),
    [mapOptionLabel],
  );

  const { technicalNames, filterOptions } = useTechnicalNameOptions({
    selectItems,
    getOptionLabel,
  });

  const selectAll = (): void => {
    const isSelectAll = getValues(name)?.length !== options?.length;
    setValue(name, isSelectAll ? options : [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const requiredComputed = useMemo(() => {
    return ValidationHelpers.hasRequiredRule(formValidation);
  }, [formValidation]);

  const {
    field: { value: formValue, onChange, ...restField },
    fieldState: { error },
  } = useController({ name, control });

  const sortedValue = useMemo(() => {
    const currentSelection = excludeNotArrayValue(formValue);
    if (disableSorting) {
      return currentSelection;
    }
    return currentSelection.toSorted((a, b) => {
      const labelA = getOptionLabel(a).toLowerCase();
      const labelB = getOptionLabel(b).toLowerCase();
      return labelA.localeCompare(labelB);
    });
  }, [formValue, disableSorting, getOptionLabel]);

  const value = useMemo(() => sortedValue, [sortedValue]);

  return (
    <FullWidthTextFieldWrapper>
      <FormControl
        fullWidth
        required={requiredComputed}
        data-e2e-template="multiselect-template"
        data-e2e-field-name={name}
        error={!!error}
      >
        <Autocomplete
          limitTags={formAttributes?.limitTagsNumber}
          {...restField}
          data-e2e={e2eData}
          data-e2e-id={`${name}_${id || ""}`}
          multiple
          disabled={disabled}
          options={options || []}
          value={value}
          slotProps={{
            listbox: {
              component: ListBox,
            },
          }}
          onChange={(_event, newValue) => {
            if (!newValue) {
              return;
            }

            checkCleanupNestedValidation(name, errors);

            onChange(newValue);
          }}
          getOptionLabel={getOptionLabel}
          filterOptions={filterOptions}
          renderOption={
            filterOptions &&
            ((props, option) =>
              DropdownOptionRenderer({
                listItemProps: props,
                option: option as string | number,
                getOptionLabel,
                technicalNames,
              }))
          }
          renderValue={
            filterOptions &&
            ((values, getItemProps) =>
              values.map((option, index) => {
                const { key, ...chipProps } = getItemProps({ index });
                return (
                  <TechnicalNameChip
                    key={key}
                    label={getOptionLabel(option)}
                    technicalName={technicalNames.get(option)}
                    chipProps={chipProps}
                  />
                );
              }))
          }
          disableCloseOnSelect
          renderInput={params => (
            <TextField
              id={params.id}
              fullWidth={params.fullWidth}
              size={params.size}
              slotProps={params.slotProps}
              error={!!error}
              label={formLabelTranslated}
              disabled={disabled}
              required={requiredComputed}
            />
          )}
          slots={{
            paper: paperProps => (
              <SelectAllPaperWrapper
                {...paperProps}
                selectAll={formValue?.length === options?.length}
                handleToggleSelectAll={() => {
                  selectAll();
                }}
              />
            ),
          }}
          filterSelectedOptions
        />
        {error && (
          <FormHelperText data-e2e="multiselect-template-error">
            {error.message}
          </FormHelperText>
        )}
      </FormControl>
      <FieldExplanationIcon formAttributes={formAttributes} />
    </FullWidthTextFieldWrapper>
  );
};
